import * as CONSTANTS from "../../constants";
import {convertStringDateToDate} from "../../../services/DateToolService";
import moment from "moment";
import config from "../../../config";

/**
 *  Booking Mutations
 */
export default {

    /**
     * Indicates that the list of accommodations is being fetched
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_FETCH_PENDING](state) {
        state.list = {...state.list, items: undefined, loading: true, noContent: false, error: false};
    },

    /**
     * Indicates that the list of accommodations has been fetched
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_FETCH_SUCCESS](state, payload) {
        state.list = {...state.list, items: payload, loading: false, noContent: false, error: false};
    },

    /**
     * Indicates that fetching the list of accommodations caused an error
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_FETCH_FAILURE](state) {
        state.list = {...state.list, items: undefined, loading: false, noContent: false, error: true};
    },

    /**
     * Indicates that the list of accommodations is empty
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_FETCH_NO_CONTENT](state) {
        state.list = {...state.list, items: undefined, loading: false, noContent: true, error: false};
    },

    /**
     * Indicates that accommodation filters has been called
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_FILTERS_PENDING](state) {
        state.filters = {...state.filters, loading: true, error: false};
    },

    /**
     * Indicates that accommodation filters has succeeded
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_FILTERS_SUCCESS] (state, payload) {
        state.filters = { ...state.filters, loading: false, error: false, sites: payload.sites, suppliers: payload.suppliers };
        state.filters.selected = {... state.filters.selected, supplier: payload.selectedSupplier}
    },

    /**
     * Indicates that accommodation filters has failed
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_FILTERS_FAILURE](state) {
        state.filters = {...state.filters, loading: false, error: true, sites: undefined};
    },

    /**
     * Decrements the current page
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_PAGE_DECREMENT](state) {
        state.list.page = state.list.page - 1;
    },

    /**
     * Increments the current page
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_PAGE_INCREMENT](state) {
        state.list.page = state.list.page + 1;
    },

    /**
     * Sets the current page
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_PAGE_SET](state, payload) {
        state.list.page = payload;
    },

    /**
     * Decrements the current page
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_PAGE_DECREMENT](state) {
        state.modal.list.page = state.modal.list.page - 1;
    },

    /**
     * Increments the current page
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_PAGE_INCREMENT](state) {
        state.modal.list.page = state.modal.list.page + 1;
    },

    /**
     * Sets the current page
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_PAGE_SET](state, payload) {
        state.modal.list.page = payload;
    },

    /**
     * Sets the selected filters
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_SELECTED_SET](state, payload) {
        state.filters.selected = {...state.filters.selected, ...payload};
    },

    /**
     * Rest the selected filters.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_SELECTED_RESET] (state) {
        state.filters.selected = {...state.filters.selected,
            site: undefined,
            accommodation: undefined,
            supplier: undefined
        };
    },

    /**
     * Sets the selected limit per page
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_LIMIT_SET](state, payload) {
        state.list.limit = payload;
    },

    /**
     * Sets the selected predicate to order by
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_PREDICATE_SET](state, payload) {
        state.list.predicate = payload;
    },

    /**
     * Sets the selected order
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_ORDER_SET](state, payload) {
        state.list.order = payload;
    },

    /**
     * Sets the display option for the allocation list.
     * - current (default)
     * - historical
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_LIST_DISPLAY_SELECT](state, payload) {
        state.list.display = payload;
    },

    /**
     * Resets the allocation list display option to the default value.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_LIST_DISPLAY_RESET](state) {
        state.list.display = 'current';
    },

    /**
     * Adds a row to the allocation form
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_FORM_ROW_ADD](state, payload) {
        state.form.rows = [
            ...state.form.rows.slice(0, payload + 1),
            state.form.defaultRow,
            ...state.form.rows.slice(payload + 1)
        ]
    },

    /**
     * Duplicates a row in the allocation form
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_FORM_ROW_DUPLICATE](state, {index, data}) {
        state.form.rows = [
            ...state.form.rows.slice(0, index + 1),
            data,
            ...state.form.rows.slice(index + 1)
        ]
    },

    /**
     * Removes a row to the allocation form
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_FORM_ROW_REMOVE](state, payload) {
        state.form.rows = state.form.rows.filter((item, index) => index !== payload);
    },

    /**
     * Clears the rows in the allocation form
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_FORM_ROW_CLEAR](state) {
        state.form.rows = [state.form.defaultRow];
    },

    /**
     * Updates a row in the allocation form
     *
     * @param state
     * @param data
     * @param index
     */
    [CONSTANTS.ALLOCATION_FORM_ROW_SET](state, {data, index}) {
        // If type is not allotment then reset the units count
        if (!(data.type === 'ALLOTMENT' || data.type === 'GUARANTEE')) {
            data.units = "";
        }
        state.form.rows = state.form.rows.map((r, i) => {
            return i === index ? data : r;
        })
    },

    /**
     * Reset the form.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_FORM_ROW_RESET](state) {
        state.form.rows = [state.form.defaultRow];
        state.form.validator = ({...state.form.validator, error: false, messages: []})
    },

    /**
     * Indicates that the form is submitted.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_FORM_SUBMIT](state, payload) {
        state.form.submit = {...state.form.submit, submitted: true, accepted: false, cancelled: false, alerts: payload};
    },

    /**
     * Indicates that the form is submitted and accepted.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_FORM_ACCEPT](state) {
        state.form.submit = {
            ...state.form.submit,
            submitted: false,
            accepted: true,
            cancelled: false,
            alerts: undefined
        };
    },

    /**
     * Indicates that the form is submitted and cancelled.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_FORM_CANCEL](state) {
        state.form.submit = {...state.form.submit, submitted: false, accepted: false, cancelled: true, alerts: undefined};
    },

    /**
     * Indicates that the list of allocations is being fetched
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_PENDING](state) {
        state.allocations = {...state.allocations, items: undefined, loading: true, noContent: false, error: false};
    },

    /**
     * Indicates that the list of allocations has been fetched
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_SUCCESS](state, payload) {
        state.allocations = {...state.allocations, items: payload, loading: false, noContent: false, error: false};
    },

    /**
     * Indicates that fetching the list of allocations caused an error
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_FAILURE](state) {
        state.allocations = {...state.allocations, items: undefined, loading: false, noContent: false, error: true};
    },

    /**
     * Indicates that the list of allocations is empty
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_FETCH_NO_CONTENT](state) {
        state.allocations = {...state.allocations, items: undefined, loading: false, noContent: true, error: false};
    },

    /**
     * Indicates that the list of merged allocations is being fetched
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_PENDING] (state) {
        state.allocationsMerged = { ...state.allocationsMerged, items: undefined, loading: true, noContent: false, error: false };
    },

    /**
     * Indicates that the list of merged allocations has been fetched
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_SUCCESS] (state, payload) {
        state.allocationsMerged = { ...state.allocationsMerged, items: payload, loading: false, noContent: false, error: false };
    },

    /**
     * Indicates that fetching the list of merged allocations caused an error
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_FAILURE] (state) {
        state.allocationsMerged = { ...state.allocationsMerged, items: undefined, loading: false, noContent: false, error: true };
    },

    /**
     * Indicates that the list of merged allocations is empty
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_MERGED_FETCH_NO_CONTENT] (state) {
        state.allocationsMerged = { ...state.allocationsMerged, items: undefined, loading: false, noContent: true, error: false };
    },

    /**
     * Decrements the current page
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_PAGE_DECREMENT](state) {
        state.allocations.page = state.allocations.page - 1;
    },

    /**
     * Increments the current page
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_PAGE_INCREMENT](state) {
        state.allocations.page = state.allocations.page + 1;
    },

    /**
     * Sets the current page
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_PAGE_SET](state, payload) {
        state.allocations.page = payload;
    },

    /**
     * Sets the allocation limit
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_LIMIT_SET](state, payload) {
        state.allocations.limit = payload;
    },

    /**
     * Set the accommodation in the state.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_SELECT](state, payload) {
        state.accommodation = payload;
    },

    /**
     * Indicates that accommodation filters has been called
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ROUTE_FILTERS_PENDING](state) {
        state.form.filters = {...state.form.filters, loading: true, error: false};
    },

    /**
     * Indicates that accommodation filters has succeeded
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ROUTE_FILTERS_SUCCESS](state, payload) {
        state.form.filters = {...state.form.filters, loading: false, error: false, sites: payload};
    },

    /**
     * Indicates that accommodation filters has failed
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ROUTE_FILTERS_FAILURE](state) {
        state.form.filters = {...state.form.filters, loading: false, error: true, sites: undefined};
    },

    /**
     * Sets the selected accommodation filters
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ROUTE_FILTERS_SELECTED_SET](state, payload) {
        state.form.filters.selected = {...state.form.filters.selected, ...payload};
    },

    /**
     * Sets the add allocations message and info to undefined.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_PENDING](state) {
        state.form.allocations = {...state.form.allocations, loading: true, error: false};
    },

    /**
     * Sets the add allocations message and info to undefined.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_SUCCESS](state) {
        state.form.allocations = {...state.form.allocations, loading: false, error: false};
    },

    /**
     * Sets the add allocations message and info to undefined.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ALLOCATIONS_ADD_FAILURE](state, payload) {
        state.form.allocations = {...state.form.allocations, loading: false, error: true, text: payload.text};
    },

    /**
     * Sets the validator error to false and message to undefined.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_FORM_VALIDATOR_SUCCESS](state) {
        state.form.validator = {...state.form.validator, error: false, messages: undefined}
    },

    /**
     * Sets the validator error to false and message to undefined.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_FORM_VALIDATOR_FAILURE](state, payload) {
        state.form.validator = {...state.form.validator, error: true, messages: payload}
    },

    /**
     * Sets the validator error to false and message to undefined.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_FORM_VALIDATOR_RESET](state) {
        state.form.validator = {...state.form.validator, error: false, messages: undefined}
    },

    /**
     * Toggle the add allocation form.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_FORM_TOGGLE](state, payload) {
        if (payload !== undefined) {
            state.form.open = payload;
        } else {
            state.form.open = !state.form.open;
        }
    },

    /**
     * Toggle manage allocation modal.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_TOGGLE](state, payload) {
        state.form.rows = [{...state.form.defaultRow, from: state.calendar.from, to: state.calendar.to}];
        if (payload !== undefined) {
            state.modal = {...state.modal, open: payload, success: false, error: false, blocked: false, adding: false};
        } else {
            state.modal.open = !state.modal.open;
            state.modal = {...state.modal, success: false, error: false, blocked: false, adding: false};
        }
        // Reset overlay success message
        state.modal.overlay.success = false;
        // if modal is closed then reset dates for the calendar
        if (!state.modal.open) {
            state.calendar = {...state.calendar, from: undefined, to: undefined, startIndex: undefined};
        }
    },

    /**
     * Set the values for the manage allocations modal form.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_FORM_SET](state, payload) {
        state.form.rows = state.form.rows.map((r, i) => {
            return i === 0 ? payload : r;
        })
    },

    /**
     * Toggle manage allocation modal.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_TOGGLE](state, payload) {
        if (payload !== undefined) {
            state.modal.overlay.open = payload;
        } else {
            state.modal.overlay.open = !state.modal.overlay.open;
        }
    },

    /**
     * Set the chosen allocation for the modal overlay.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_ALLOCATION_SET](state, payload) {
        if (payload.releaseDate) {
            payload.releaseMethod = 'date'
        } else {
            payload.releaseMethod = 'days'
        }
        state.modal.overlay = {...state.modal.overlay, loading: false, error: false, success: false};
        state.modal.overlay.allocation.original = payload;
        // convert the original date if present to use on the copied item
        if (payload.releaseDate !== null && payload.releaseDate.indexOf('/') === -1) {
            payload.releaseDate = convertStringDateToDate(payload.releaseDate).format('DD/MM/YYYY');
        }
        state.modal.overlay.allocation.item = payload;
    },

    /**
     * Indicates that allocation update is pending.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_PENDING](state) {
        state.modal.overlay = {...state.modal.overlay, loading: true, error: false, success: false, message: undefined};
    },

    /**
     * Indicates that allocation update is successful.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_SUCCESS](state, payload) {
        state.modal.overlay = {...state.modal.overlay, loading: false, error: false, success: true, message: payload};
    },

    /**
     * Indicates that allocation update failed.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_UPDATE_FAILURE](state, payload) {
        state.modal.overlay = {...state.modal.overlay, loading: false, error: true, success: false, message: payload};
    },

    /**
     * Sets the overlay allocation data.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_FORM_SET](state, payload) {
        if (payload.hasOwnProperty('type')) {
            if (payload.type === 'ALLOTMENT') {
                payload.units = state.modal.overlay.allocation.original.units || 1;
            } else {
                payload.units = null;
            }
        }
        if (payload.hasOwnProperty('releaseDays') && payload.releaseDays === '') {
            payload.releaseDays = 0;
        }
        if(payload.hasOwnProperty('units') && isNaN(payload.units)) {
            payload.units = 1;
        }
        state.modal.overlay.allocation.item = {...state.modal.overlay.allocation.item, ...payload};
    },

    /**
     * Reset the allocation item.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_FORM_RESET](state) {
        state.modal.overlay.allocation.item = {
            ...state.modal.overlay.allocation.item,
            ...state.modal.overlay.allocation.original
        }
    },

    /**
     * Indicates that the update allocation data is valid.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_VALIDATOR_SUCCESS](state) {
        state.modal.overlay.validator = {...state.modal.overlay.validator, error: false, messages: undefined}
    },

    /**
     * Indicates that the update allocation data is invalid.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_VALIDATOR_FAILURE](state, payload) {
        state.modal.overlay.validator = {...state.modal.overlay.validator, error: true, messages: payload}
    },

    /**
     * Resets the modal form.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_FORM_RESET](state) {
        state.form.rows = [{...state.form.defaultRow, from: state.calendar.from, to: state.calendar.to}];
        state.form.validator = ({...state.form.validator, error: false, messages: []})
    },

    /**
     * Increment the overlay bookings page.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_INCREMENT](state) {
        state.modal.overlay.bookings.page = state.modal.overlay.bookings.page + 1;
    },

    /**
     * Decrement the overlay bookings page.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_DECREMENT](state) {
        state.modal.overlay.bookings.page = state.modal.overlay.bookings.page - 1;
    },

    /**
     * Set the overlay bookings page.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_BOOKINGS_PAGE_SET](state, payload) {
        state.modal.overlay.bookings.page = payload;
    },

    /**
     * Reset modal errors
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_MODAL_OVERLAY_ERROR_MESSAGE_RESET](state) {
        state.form.allocations.error = false;
        state.form.allocations.text = undefined;
    },

    /**
     * Sets the dates for the manage allocation calendar.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_CALENDAR_DATE_SET](state, payload) {
        state.calendar = {...state.calendar, ...payload.data};
        // Set the first row
        state.form.rows = [{...state.form.defaultRow, from: state.calendar.from, to: state.calendar.to}];
    },

    /**
     * Increment the calendar page by 3 as it shows 3 month per row.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_CALENDAR_PAGE_INCREMENT](state) {
        state.calendar.startMonth = state.calendar.startMonth - 3;
        state.calendar.endMonth = state.calendar.endMonth - 3;
    },

    /**
     * Decrement the calendar page by 3 as it shows 3 month per row.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_CALENDAR_PAGE_DECREMENT](state) {
        state.calendar.startMonth = state.calendar.startMonth + 3;
        state.calendar.endMonth = state.calendar.endMonth + 3;
    },

    /**
     * Chose calendar display option.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_CALENDAR_DISPLAY_SET](state, payload) {
        state.calendar.display.choice = payload;
    },

    /**
     * Toggle the calendar infobox.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_CALENDAR_INFO_TOGGLE](state, payload) {
        if (payload !== undefined) {
            state.calendar.showInfo = payload;
        } else {
            state.calendar.showInfo = !state.calendar.showInfo;
        }
    },

    /**
     * Change the info page number.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_CALENDAR_INFO_PAGE_SET](state, payload) {
        state.calendar.infoPage = payload;
    },

    /**
     * Toggle the modal add allocation form.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_MODAL_ADD_TOGGLE](state, payload) {
        if (payload !== undefined) {
            state.modal.adding = payload;
        } else {
            state.modal.adding = !state.modal.adding;
        }
    },

    /**
     * Indicates that allocation calendar is being fetched.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_CALENDAR_FETCH_PENDING](state) {
        state.calendar = {...state.calendar, items: undefined, loading: true, error: false}
    },

    /**
     * Indicates that allocation calendar failed to load.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_CALENDAR_FETCH_FAILURE](state) {
        state.calendar = {...state.calendar, items: undefined, loading: false, error: true}
    },

    /**
     * Indicates that allocation calendar is successfully fetched.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_CALENDAR_FETCH_SUCCESS](state, payload) {
        state.calendar = {...state.calendar, items: payload, loading: false, error: false}
    },

    /**
     * Change the year.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_YEAR_SET](state, payload) {
        state.allocations.years.current = payload;
    },

    /**
     * Indicate that request accept is loading.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_UPDATE_PENDING](state) {
        state.request = {...state.request, loading: true, error: false}
    },

    /**
     * Indicate that request accept is successful.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_UPDATE_SUCCESS](state) {
        state.request = {...state.request, loading: false, error: false}
    },

    /**
     * Indicate that request accept is failed.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_UPDATE_FAILURE](state) {
        state.request = {...state.request, loading: false, error: true}
    },

    /**
     * Edit the request object.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_REQUEST_CHANGE](state, payload) {
        state.request.pending[payload.index].changes = {...state.request.pending[payload.index].changes, ...payload.value}
    },

    /**
     * Indicate that the reqeust list is being loaded.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_LIST_PENDING](state) {
        state.request = {...state.request, loading: true, noContent: false, error: false, items: undefined}
    },

    /**
     * Indicates that the request list was loaded successfully.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_REQUEST_LIST_SUCCESS](state, payload) {
        state.request = {...state.request, loading: false, noContent: false, error: false, items: payload};
    },

    /**
     * Indicates that the request list failed to load.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_LIST_FAILURE](state) {
        state.request = {...state.request, loading: false, noContent: false, error: true, items: undefined};
    },

    /**
     * Indicates that the request list had no content.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_LIST_NO_CONTENT](state) {
        state.request = {...state.request, loading: false, noContent: true, error: false, items: undefined};
    },

    /**
     * Reset the allocation timer.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_COUNTDOWN_RESET](state) {
        state.timer = 10;
    },

    /**
     * Decrement allocation timer.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_COUNTDOWN_DECREMENT](state) {
        state.timer--;
    },

    /**
     * Indicates that the allocation data is being fetched.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_DATA_FETCH_PENDING](state) {
        state.data = {...state.data, loading: true, error: false, noContent: false, message: undefined};
    },

    /**
     * Indicates that the allocation data was fetched successfully.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_DATA_FETCH_SUCCESS](state) {
        state.data = {...state.data, loading: false, error: false, noContent: false, message: undefined};
    },

    /**
     * Indicsates that the allocation data was fetched unsuccessfully.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_DATA_FETCH_FAILURE](state, payload) {
        state.data = {...state.data, loading: false, error: true, noContent: false, message: payload};
    },

    /**
     * Sets the request predicate.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_REQUEST_PREDICATE_SET](state, payload) {
        state.request.predicate = payload;
    },

    /**
     * Resets the request page.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_PAGE_RESET](state) {
        state.request.page = 1;
    },

    /**
     * Increments the request page.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_PAGE_INCREMENT](state) {
        state.request.page++;
    },

    /**
     * Decrement the request page.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_PAGE_DECREMENT](state) {
        state.request.page--;
    },

    /**
     * Selects a request item.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_REQUEST_ITEM_SELECT](state, payload) {
        // Convert dates
        payload.request.from = convertStringDateToDate(payload.request.from).format('DD/MM/YYYY');
        payload.request.to = convertStringDateToDate(payload.request.to).format('DD/MM/YYYY');
        if (payload.request.changes.release.releaseDate) {
            payload.request.changes.release.releaseDate = convertStringDateToDate(payload.request.changes.release.releaseDate).format('DD/MM/YYYY');
        }
        state.request.item = payload.request;
        state.request.itemIndex = payload.index;
        state.request.indices = payload.indices;
    },

    /**
     * Unselect request item.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_REQUEST_ITEM_DESELECT](state) {
        state.request.item = undefined;
        state.request.itemIndex = undefined;
        state.request.indices = [];
    },

    /**
     * Updates the selected request item.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_REQUEST_ITEM_SET](state, payload) {
        state.request.item.changes = {...state.request.item.changes, ...payload};
    },

    /**
     * Toggles the edit page-
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_REQUEST_EDIT_TOGGLE](state, payload) {
        if (payload !== undefined) {
            state.request.editMode = payload;
        } else {
            state.request.editMode = !state.request.editMode;
        }
    },

    /**
     * Set the dates for the calendar.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_CALENDAR_DATES_SET](state, payload) {
        state.allocations.years.current = payload.year;
        state.calendar = {...state.calendar, ...payload};
    },

    /**
     * Indicates that the arrival rules was successfully fetched.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ARRIVAL_LIST_PENDING] (state) {
        state.arrivalRules = {...state.arrivalRules, loading: true, error: false, noContent: false}
    },

    /**
     * Indicates that the arrival rules was successfully fetched.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_ARRIVAL_LIST_SUCCESS] (state, payload) {
        state.arrivalRules = {...state.arrivalRules, loading: false, error: false, noContent: false, item: payload}
    },

    /**
     * Indicates that the arrival rules was unsuccessfully fetched.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ARRIVAL_LIST_FAILURE] (state) {
        state.arrivalRules = {...state.arrivalRules, loading: false, error: true, noContent: false}
    },

    /**
     * Indicates that there were no arrival rules.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_ARRIVAL_LIST_NO_CONTENT] (state) {
        state.arrivalRules = {...state.arrivalRules, loading: false, error: false, noContent: true}
    },

    /**
     * Add allocation comment.
     *
     * @param state
     * @param payload
     */
    [CONSTANTS.ALLOCATION_COMMENT_SET] (state, payload) {
        state.modal.overlay.allocation.item = {...state.modal.overlay.allocation.item, comment: payload}
    },

    /**
     * Reset allocation comment and element.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_COMMENT_RESET] (state) {
        state.modal.overlay.allocation.item = {
            comment: undefined
        }
    },

    /**
     * Reset the allocations state.
     *
     * @param state
     */
    [CONSTANTS.ALLOCATION_RESET_STATE](state) {
        state.data = {...state.data, loading: false, error: false, noContent: false, message: undefined};
        state.list = {
            ...state.list,
            loading: false,
            noContent: false,
            error: false,
            items: undefined,
            predicate: "pendingRequests",
            predicateList: ["pendingRequests", "name", "updatedAt", "site"],
            limit: 20,
            limitList: [5, 10, 20, 50, 100, 200],
            page: 1,
            display: 'current',
            displayOptions: ['all', 'current', 'historical'],
            orderList: ["asc", "desc"],
            order: "desc"
        };
        state.allocations = {
            ...state.allocations, loading: false,
            noContent: false,
            error: false,
            items: undefined,
            years: {
                current: moment().startOf('year').format('YYYY'),
                years: [moment().startOf('year').format('YYYY'), moment().add(1, 'years').startOf('year').format('YYYY'), moment().add(2, 'years').startOf('year').format('YYYY')]
            },
            limit: 10,
            limitList: [5, 10, 20, 50, 100, 200],
            page: 1,
            orderList: ["asc", "desc"],
            order: "asc"
        };
        state.allocationsMerged = {...state.allocationsMerged, loading: false, noContent: false, error: false, items: undefined};
        state.filters = {...state.filters, loading: false, error: false, sites: {}};
        state.filters.selected = {...state.filters.selected, site: undefined, accommodation: undefined, supplier: undefined};
        state.form = {...state.form, open: true};
        state.form.defaultRow = {
            ...state.form.defaultRow,
            from: undefined,
            to: undefined,
            type: "ALLOTMENT",
            units: undefined,
            releaseType: undefined,
            releaseMethod: 'days',
            releaseDays: undefined,
            releaseDate: undefined
        };
        state.form.filters = {...state.form.filters, sites: {}, loading: false, error: false};
        state.form.filters.selected = {...state.form.filters.selected, site: undefined, accommodation: undefined};
        state.form.allocations = {...state.form.allocations, loading: false, error: false};
        state.form.validator = {...state.form.validator, error: false, message: undefined};
        state.form.submit = {...state.form.submit, submitted: false, accepted: false, cancelled: false, alerts: null};
        state.calendar = {
            ...state.calendar,
            loading: false,
            error: false,
            from: undefined,
            to: undefined,
            startIndex: undefined,
            fullCalendar: false,
            showInfo: false,
            infoPage: 1,
            startMonth: 3,
            endMonth: 9,
            items: undefined
        };
        state.calendar.display = {...state.calendar.display, choices: ['full', 'months'], choice: 'months'};
        state.allocationPeriod = {...state.allocationPeriod, from: config.business.SEASON_DATE_START, to: config.business.SEASON_DATE_END};
        state.accommodation = {...state.accommodation, id: undefined, siteId: undefined};
        state.modal = {...state.modal, open: false, loading: undefined, noContent: undefined, error: undefined, success: false, blocked: false, adding: false};
        state.modal.list = {...state.modal.list, limit: 3, page: 1};
        state.modal.overlay = {...state.modal.overlay, loading: false, error: false, success: false, open: false, message: undefined};
        state.modal.overlay.allocation = {...state.modal.overlay.allocation, item: undefined, original: undefined, changes: false};
        state.modal.overlay.validator = {...state.modal.overlay.validator, error: false, messages: undefined};
        state.modal.overlay.bookings = {...state.modal.overlay.bookings, page: 1, limit: 3};
        state.request = {
            ...state.request,
            editMode: false,
            loading: false,
            success: false,
            error: false,
            noContent: false,
            message: 'accept.success',
            item: undefined,
            itemIndex: undefined,
            indices: [],
            limit: 3,
            page: 1,
            predicate: 'pending',
            predicates: ['pending', 'approved', 'denied', 'modified']
        };
        state.request.items = {...state.request.items, pending: [], approved: [], denied: [], modified: []};
    }
}



// WEBPACK FOOTER //
// ./src/store/modules/allocation/mutations.js